import { Address } from '@guardfinder/address';

import { JOB_TYPES, REPEAT_CYCLE } from '../../../../../../types';
import {
    CreateJobAdditionalInformation,
    CreateJobEventInformation,
    CreateJobUniform,
    Job
} from '../../../../Domain/Model/CreateJob/create-job.model';

export class CreateJobRequest {
    constructor(
        public readonly job_type: string,
        public readonly start_date: string,
        public readonly start_time: string,
        public readonly number_of_guards: number,
        public readonly number_of_hours: number,
        public readonly primary_contact_name: string,
        public readonly primary_contact_phone: string,
        public readonly primary_contact_email: string,
        public readonly job_description: string,
        public readonly rate: number,
        public readonly location: {
            address: string;
            city: string;
            postal_code: string;
            province?: string;
        },
        public readonly end_date?: string,
        public readonly event_type_id?: number,
        public readonly event_details?: string,
        public readonly previous_security_issues?: string,
        public readonly emergency_procedures?: string,
        public readonly primary_guard_duties?: string,
        public readonly guard_armed_status?: string,
        public readonly uniform_type?: string,
        public readonly uniform_provider?: string,
        // Ongoing job fields
        public readonly repeat_cycle?: string,
        public readonly repeat_type?: string,
        public readonly repeat_cycle_frequency?: number,
        public readonly repeat_day?: string,
        public readonly repeat_end_date?: string
    ) {}

    /**
     * Maps a Job domain model to a CreateJobRequest DTO
     * @param model The Job domain model
     * @returns A new CreateJobRequest instance
     */
    static fromModel(model: Job): CreateJobRequest {
        if (!model.isJobHourlyRateSet()) {
            throw new Error('Job hourly rate is required');
        }

        // Map basic job information
        const basicInfo = this.mapBasicJobInfo(model);

        // Map location information
        const location = this.mapLocation(model.eventInformation.eventLocationAddress);

        // Map additional information
        const additionalInfo = this.mapAdditionalInfo(model.additionalInformation);

        // Map event information
        const eventInfo = this.mapEventInfo(model.eventInformation);

        // Map uniform information
        const uniformInfo = this.mapUniformInfo(model.uniform);

        // Map ongoing job information
        const ongoingJobInfo = this.mapOngoingJobInfo(model);

        return new CreateJobRequest(
            basicInfo.job_type,
            basicInfo.start_date,
            basicInfo.start_time,
            basicInfo.number_of_guards,
            basicInfo.number_of_hours,
            model.primaryContact.name,
            model.primaryContact.phone,
            model.primaryContact.email,
            additionalInfo.job_description,
            model.getJobHourlyRate(),
            location,
            ongoingJobInfo.end_date,
            eventInfo.event_type_id,
            eventInfo.event_details,
            additionalInfo.previous_security_issues,
            additionalInfo.emergency_procedures,
            additionalInfo.primary_guard_duties,
            eventInfo.guard_armed_status,
            uniformInfo.uniform_type,
            uniformInfo.uniform_provider,
            ongoingJobInfo.repeat_cycle,
            ongoingJobInfo.repeat_type,
            ongoingJobInfo.repeat_cycle_frequency,
            ongoingJobInfo.repeat_day,
            ongoingJobInfo.repeat_end_date
        );
    }

    /**
     * Maps basic job information from the Job model
     */
    private static mapBasicJobInfo(model: Job): {
        job_type: string;
        start_date: string;
        start_time: string;
        number_of_guards: number;
        number_of_hours: number;
    } {
        return {
            job_type: model.type,
            start_date: model.date.startDate.toISODate() || '',
            start_time: model.date.startDate.toFormat('HH:mm:ss'),
            number_of_guards: model.guards.guardsCount,
            number_of_hours: model.guards.hoursGuardPerDay
        };
    }

    /**
     * Maps location information from the Address model
     */
    private static mapLocation(address: Address): {
        address: string;
        city: string;
        postal_code: string;
        province?: string;
    } {
        return {
            address: `${address.street_number} ${address.street}`,
            city: address.city,
            postal_code: address.zip,
            province: address.state
        };
    }

    /**
     * Maps additional information from the CreateJobAdditionalInformation model
     */
    private static mapAdditionalInfo(info: CreateJobAdditionalInformation): {
        job_description: string;
        previous_security_issues?: string;
        emergency_procedures?: string;
        primary_guard_duties?: string;
    } {
        return {
            job_description: info.jobDescription,
            previous_security_issues: info.previousIssues,
            emergency_procedures: info.emergencyProcedures,
            primary_guard_duties: info.guardPrimaryDuties
        };
    }

    /**
     * Maps event information from the CreateJobEventInformation model
     */
    private static mapEventInfo(info: CreateJobEventInformation): {
        event_type_id: number;
        event_details?: string;
        guard_armed_status?: string;
    } {
        return {
            event_type_id: info.eventType.id
        };
    }

    /**
     * Maps uniform information from the CreateJobUniform model
     */
    private static mapUniformInfo(uniform?: CreateJobUniform): {
        uniform_type?: string;
        uniform_provider?: string;
    } {
        if (!uniform) {
            return {};
        }

        return {
            uniform_type: uniform.uniformType,
            uniform_provider: uniform.providedBy
        };
    }

    /**
     * Maps ongoing job information from the Job model
     */
    private static mapOngoingJobInfo(model: Job): OngoingJobInfo {
        if (model.type !== JOB_TYPES.ONGOING || !model.ongoingJobDetails) {
            return {} as OngoingJobInfo;
        }

        if (!model.ongoingJobDetails.ongoingJobEndDate) {
            throw new Error('End date is required for an ongoing job');
        }

        const end_date = model.ongoingJobDetails.ongoingJobEndDate.toISODate();
        if (!end_date) {
            throw new Error('End date has invalid format');
        }

        const result: OngoingJobInfo = {
            end_date,
            repeat_cycle: model.ongoingJobDetails.repeatCycle
        };

        if (model.ongoingJobDetails.repeatCycle === REPEAT_CYCLE.CUSTOM) {
            result.repeat_type = model.ongoingJobDetails.repeatCycleCustomType;

            if (model.ongoingJobDetails.repeatCycleCustomType === 'day') {
                const repeatDays = model.ongoingJobDetails.repeatDays;
                if (!repeatDays) {
                    throw new Error('Repeat Days is required for this job');
                }
                result.repeat_day = repeatDays.join(',');
            } else {
                result.repeat_cycle_frequency = model.ongoingJobDetails.repeatCycleFrequencyAmount;
            }
        }

        if (model.ongoingJobDetails.repeatEndDate) {
            result.repeat_end_date = model.ongoingJobDetails.repeatEndDate.toISODate() || '';
        }

        return result;
    }
}

interface OngoingJobInfo {
    end_date: string;
    repeat_cycle?: string;
    repeat_type?: string;
    repeat_cycle_frequency?: number;
    repeat_day?: string;
    repeat_end_date?: string;
}
